import { AutorenewRounded, BuildRounded, CloudSyncRounded, FastForwardRounded, HealthAndSafety, KeyOff, MoveDownRounded, StarRounded, StoreRounded } from "@mui/icons-material";
import { Stack } from "@mui/material";
import { ColDef, ColumnMovedEvent, ColumnVisibleEvent, GridReadyEvent, ICellRendererParams, IServerSideDatasource, SideBarDef, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import * as React from "react";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import { useCallback, useContext, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import AgGridCustomTable from "../../../../../../util/component/AgGridCustomTable";
import { getPendingList, IGetPendingListReturn, IPendingListRow } from "../../../../api/services";
import { getPendingListCount } from "../../../../api/services/getPendingListCount";
import { IBookingsApiFilter, ISortBy } from "../../../../api/types";
import { BookingActionStatus, VipType } from "../../../../types";
import { pendingListCountState, pendingListFilterState } from "../../recoil";
import CellActions from "./cell-actions/CellActions";
import CellAvailability from "./cell-availability/CellAvailability";
import CellStatus from "./cell-status/CellStatus";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import { setDecimalSeparator } from "../../../../utils";
import CustomLoader from "../../../../../../util/component/CustomLoader";
import CellBookingAction from "./cell-booking-actions/CellBookingAction";

const TablePendingList = () => {

    const gridRef = useRef<AgGridReact>(null);

    const { t, i18n } = useTranslation('booking');

    const { globalData } = useContext(GlobalContext);

    const pendingListFilter = useRecoilValue(pendingListFilterState);
    const setPendingListCount = useSetRecoilState(pendingListCountState);

    const pendingListMutation = useMutation(getPendingList);
    const pendingListCountMutation = useMutation(getPendingListCount);

    const columnStateStorageKey = useMemo(() => {
        return `__BOOKING_PENDING.tableColumsConfig:${globalData.user.username}`;
    }, [globalData.user.username]);

    const columnDefs = useMemo<ColDef<IPendingListRow>[]>(() => [
        {
            field: 'tags',
            headerName: t('label.tags'),
            pinned: 'left',
            minWidth: 120,
            suppressSizeToFit: true,
            suppressColumnsToolPanel: true,
            resizable: false,
            cellStyle: {
                display: 'flex',
                alignItems: 'center'
            },
            cellRenderer: (params: ICellRendererParams<IPendingListRow>) => {
                return (
                    <Stack direction="row" alignItems="center">
                        {params.data && [VipType.Internal, VipType.External, VipType.VipCS].includes(params.data.booking.vip) &&
                            <StarRounded
                                titleAccess={t('label.vip')}
                                color="secondary"
                            />
                        }
                        {params.data?.booking.fastLine &&
                            <FastForwardRounded
                                titleAccess={t('label.fastLine')}
                                color="secondary"
                            />
                        }
                        {params.data?.booking.carSwap &&
                            <CloudSyncRounded
                                titleAccess={t('label.carSwap')}
                                color="secondary"
                            />
                        }
                        {params.data?.booking.substitution &&
                            <BuildRounded
                                titleAccess={t('label.substitution')}
                                color="secondary"
                            />
                        }
                        {params.data?.booking.customerAtDesk &&
                            <StoreRounded
                                titleAccess={t('label.customerAtDesk')}
                                color="secondary"
                            />
                        }
                        {params.data?.booking.renewal &&
                            <AutorenewRounded
                                titleAccess={t('label.renewal')}
                                color="secondary"
                            />
                        }
                        {params.data?.booking.recovery &&
                            <HealthAndSafety
                                titleAccess={t('label.recovery')}
                                color="secondary"
                            />
                        }
                        {params.data?.booking.packageSwap &&
                            <MoveDownRounded
                                titleAccess={t('label.packageSwap')}
                                color="secondary"
                            />
                        }
                        {params.data?.booking.keyless &&
                            <KeyOff
                                titleAccess={t('label.keyless')}
                                color="secondary"
                            />
                        }
                    </Stack>
                );
            }
        },
        {
            field: 'id',
            headerName: t('label.bookingNumber'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return params.data?.booking.id ?? '-'
            },
        },
        {
            field: 'requestCreationDate',
            headerName: t('label.creationDate'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return params.data?.booking.requestCreationDate ? dayjs(params.data.booking.requestCreationDate).format('DD-MM-YYYY HH:mm') : '-'
            },
        },
        {
            field: 'category',
            headerName: t('label.category'),
            minWidth: 200,
            sortable: true,
            valueGetter: (params: ValueGetterParams<IPendingListRow>) => {
                return params.data?.booking.categoryLabel ?? '-';
            },
        },
        {
            field: 'segment',
            headerName: t('label.segment'),
            minWidth: 200,
            sortable: true,
            valueGetter: (params: ValueGetterParams<IPendingListRow>) => {
                return params.data?.booking.segmentLabel ?? '-';
            },
        },
        {
            field: 'bookingOwner',
            headerName: t('label.bookingOwner'),
            minWidth: 200,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return `${params.data?.booking.bookingOwner ? `${params.data.booking.bookingOwner.surname} ${params.data.booking.bookingOwner.name}` : params.data?.booking.bookingCompanyOwner ? params.data.booking.bookingCompanyOwner.companyName : ''}`
            }
        },
        {
            field: 'driver',
            headerName: t('label.mainDriver'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return params.data?.booking.firstDriver ? `${params.data.booking.firstDriver.surname} ${params.data.booking.firstDriver.name}` : '-';
            },
        },
        {
            field: 'driverBirthDate',
            headerName: t('label.mainDriverBirthDate'),
            minWidth: 200,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return params.data?.booking.firstDriver?.birthDate ? dayjs(params.data.booking.firstDriver.birthDate).format('DD/MM/YYYY') : '-';
            }
        },
        {
            field: 'acriss',
            headerName: t('label.acriss'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return (params.data?.booking.carGroup.acrissCode ?? '-');
            }
        },
        {
            field: 'sipp',
            headerName: t('label.sipp'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return (params.data?.booking.carGroup.sippCode ?? '-');
            }
        },
        {
            field: 'pickupLocation',
            headerName: t('label.pickupLocation'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return params.data?.booking.pickupLocation.label ?? '-';
            }
        },
        {
            field: 'pickupDateTime',
            headerName: t('label.pickupDateTime'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return params.data?.booking.pickupDateLocalISO ? dayjs(params.data.booking.pickupDateLocalISO).format('DD-MM-YYYY HH:mm') : '-'
            }
        },
        {
            field: 'dropoffLocation',
            headerName: t('label.dropoffLocation'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return params.data?.booking.dropoffLocation.label ?? '-';
            }
        },
        {
            field: 'dropoffDateTime',
            headerName: t('label.dropoffDateTime'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return params.data?.booking.dropoffDateLocalISO ? dayjs(params.data.booking.dropoffDateLocalISO).format('DD-MM-YYYY HH:mm') : '-'
            }
        },
        {
            field: 'totalRentalDays',
            headerName: t('label.rentalDays'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return String(params.data?.booking.totalRentalDays) ?? '-';
            }
        },
        {
            field: 'totalToBePaid',
            headerName: t('label.totalAmount'),
            minWidth: 200,
            valueFormatter: (params: ValueFormatterParams<IPendingListRow>) => {
                return `${setDecimalSeparator(((params.data?.booking.totalToBePaid ?? 0) / 100).toFixed(2), i18n.language)} €`;
            }
        },
        {
            field: 'pendingReasons',
            headerName: t('label.pendingReasons'),
            minWidth: 300,
            sortable: false,
            cellRenderer: (params: ICellRendererParams<IPendingListRow>) => {
                return (
                    params.data?.booking.checkBlackList ?
                        t('label.watchlist') :
                        params.data?.booking.rules.map((r) => `${r.name}`).join(', ')
                )
            }
        },
        {
            field: 'keyless',
            headerName: t('label.keyless'),
            minWidth: 200,
            sortable: true,
            cellRenderer: (params: any) => {
                return t(params.data?.booking.keyless ? 'label.yes' : 'label.no')
            }
        },
        {
            field: 'carAvailability',
            headerName: '',
            minWidth: 70,
            pinned: 'right',
            resizable: false,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
            cellStyle: {
                display: 'flex',
                alignItems: 'center'
            },
            cellRenderer: CellAvailability
        },
        {
            field: 'statusSubstatus',
            headerName: t('label.statusSubstatus'),
            minWidth: 240,
            pinned: 'right',
            suppressMovable: true,
            resizable: false,
            suppressColumnsToolPanel: true,
            cellRenderer: CellStatus
        },
        {
            field: 'bookingAction',
            headerName: t('label.bookingAction'),
            minWidth: 200,
            pinned: 'right',
            suppressMovable: true,
            resizable: false,
            suppressColumnsToolPanel: true,
            cellRenderer: CellBookingAction
        },
        {
            field: '_actions',
            headerName: '',
            minWidth: 100,
            pinned: 'right',
            resizable: false,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
            cellRenderer: CellActions,
        }
    ], [t]);


    const defaultColDef = useMemo<ColDef>(() => ({
        lockPinned: true,
        resizable: true,
        unSortIcon: true
    }), []);
    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                const filters: IBookingsApiFilter = {
                    ...pendingListFilter,
                    statuses: ['PENDING']
                }
                const sortBy: ISortBy | undefined = params.request.sortModel[0] ? {
                    field: params.request.sortModel[0].colId,
                    order: params.request.sortModel[0].sort
                } : undefined;
                const start = params.request.startRow;
                const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;

                let pendingListCount : number;
                pendingListCountMutation.mutateAsync({
                    filters
                }).then((response: number) => {
                    pendingListCount = response
                    setPendingListCount(pendingListCount);

                    pendingListMutation.mutateAsync({
                        filters,
                        sortBy,
                        start,
                        limit
                    }).then((response: IGetPendingListReturn) => {
                        params.success({
                            rowData: response.rows,
                            rowCount: pendingListCount ?? 0
                        });

                        if (pendingListCount === 0) {
                            params.api.showNoRowsOverlay();
                        }
                    }).catch((err: AxiosError) => {
                            params.fail();
                            params.api.hideOverlay();
                        });
                }).catch((err: AxiosError) => {
                    params.fail();
                    params.api.hideOverlay();
                });
            }
            catch {
                params.fail();
                params.api.hideOverlay();
            }
        }
    }), [pendingListFilter]);

    const sideBar = useMemo<SideBarDef | string | string[] | boolean | null>(() => ({
        toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivots: true,
                    suppressPivotMode: true,
                    suppressColumnExpandAll: true,
                },
            },
        ],
        defaultToolPanel: '',
    }), []);

    const onGridReady = useCallback((event: GridReadyEvent) => {
        const colState = JSON.parse(localStorage.getItem(columnStateStorageKey) || '[]');
        event.columnApi.applyColumnState({
            state: colState,
            applyOrder: true
        });
    }, [columnStateStorageKey]);

    const onColumnVisible = useCallback((event: ColumnVisibleEvent) => {
        localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
    }, [columnStateStorageKey]);
    const onColumnMoved = useCallback((event: ColumnMovedEvent) => {
        localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
    }, [columnStateStorageKey]);

    return (
        <>
            <AgGridCustomTable
                gridRef={gridRef}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                serverSideDatasource={serverSideDatasource}
                rowModelType="serverSide"
                serverSideInfiniteScroll
                paginationPageSize={25}
                cacheBlockSize={25}
                sideBar={sideBar}
                gridOptions={{
                    getRowStyle(params){
                        if(params.data?.booking){
                            switch(params.data.booking.pendingAction){
                                case BookingActionStatus.BeConfirmed:
                                    return {'background-color': '#98db85'}
                                case BookingActionStatus.BeRejected:
                                    return {'background-color': '#ea8585'}
                                default:
                                    return
                            }
                        }
                    }
                }}
                suppressDragLeaveHidesColumns
                suppressMultiSort
                enableCellTextSelection
                onGridReady={onGridReady}
                onColumnVisible={onColumnVisible}
                onColumnMoved={onColumnMoved}
            />
            <CustomLoader
                loading={pendingListMutation.isLoading ||
                pendingListCountMutation.isLoading}
            />
        </>
    );
};

export default TablePendingList;
